// src/Services/userActivity.service.js
import config from "../config";
import selectedEnvironment from "../env";
const BASEAPI = config[selectedEnvironment].API_URL;

const API_URL = `${config[selectedEnvironment].API_URL}/UserActivity`;

/**
 * Fetch total activity count for a single user.
 * @param {{ UserId: string, Start: string, End: string, ActivityType?: number, CaseId?: string }} payload
 * @param {string} authToken
 */
const GetTotalActivity = async (payload, authToken) => {
    const response = await fetch(`${API_URL}/total`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Fetch sub-accountants' totals for a given accountant.
 * @param {{ AccountantId: string, Start: string, End: string, ActivityType?: number, CaseId?: string }} payload
 * @param {string} authToken
 */
const GetSubAccountantsTotals = async (payload, authToken) => {
    const response = await fetch(`${API_URL}/sub-totals`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Fetch daily summaries (per user, per day buckets).
 * @param {{ UserId?: string, AccountantId?: string, ActivityType?: number, StartUtc: string, EndUtc: string, CaseId?: string }} payload
 * @param {string} authToken
 */
const GetDailySummaries = async (accountantId, payload, authToken) => {
    const { subAccountantIds = [], clientCaseIds = [], ...rest } = payload;
    const userIds = subAccountantIds.map((sa) => sa.value);
    const caseIds = clientCaseIds.map((c) => c.value);
    const body = {
        ...rest,
        accountantId,
        userIds,
        caseIds,
    };

    const response = await fetch(`${API_URL}/summaries`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });
    return response;
};

/**
 * Fetch per-user, per-day breakdowns.
 * @param {{ UserId?: string, AccountantId?: string, ActivityType?: number, StartUtc: string, EndUtc: string, CaseId?: string }} payload
 * @param {string} authToken
 */
const GetDailyByUser = async (payload, authToken) => {

    const { subAccountantIds = [], clientCaseIds = [], ...rest } = payload;
    const userIds = subAccountantIds.map((sa) => sa.value);
    const caseIds = clientCaseIds.map((c) => c.value);
    const body = {
        ...rest,
        userIds,
        caseIds,
    };

    const response = await fetch(`${API_URL}/daily-by-user`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });
    return response;
};


const FilterDailyMouseRecords = async (payload, authToken) => {
  const url = `${BASEAPI}/DailyMouseRecords/filter`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  return response;
};


/**
 * Generate employee efficiency report
 * @param {{ AccountantId: string, StartUtc: string, EndUtc: string, SubAccountantIds?: string[], CaseIds?: string[] }} payload
 * @param {string} authToken
 */
const GetEmployeeEfficiencyReport = async (payload, authToken) => {
    const response = await fetch(`${API_URL}/efficiency-report`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Generate business efficiency report
 * @param {{ AccountantId: string, StartUtc: string, EndUtc: string, CaseIds?: string[] }} payload
 * @param {string} authToken
 */
const GetBusinessEfficiencyReport = async (payload, authToken) => {
    const response = await fetch(`${API_URL}/business-efficiency-report`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Generate employee efficiency report HTML
 * @param {{ AccountantId: string, StartUtc: string, EndUtc: string, SubAccountantIds?: string[], CaseIds?: string[] }} payload
 * @param {string} authToken
 */
const GetEmployeeEfficiencyReportHtml = async ( authToken,payload) => {
    const response = await fetch(`${API_URL}/efficiency-report-html`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Generate business efficiency report HTML
 * @param {{ AccountantId: string, StartUtc: string, EndUtc: string, CaseIds?: string[] }} payload
 * @param {string} authToken
 */
const GetBusinessEfficiencyReportHtml = async (authToken,payload) => {
    const response = await fetch(`${API_URL}/business-efficiency-report-html`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Get Employee Efficiency Report Excel
 * @param {string} authToken
 * @param {object} payload
 */
const GetEmployeeEfficiencyReportExcel = async (authToken, payload) => {
    const response = await fetch(`${API_URL}/employee-efficiency-report-excel`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Get Business Efficiency Report Excel
 * @param {string} authToken
 * @param {object} payload
 */
const GetBusinessEfficiencyReportExcel = async (authToken, payload) => {
    const response = await fetch(`${API_URL}/business-efficiency-report-excel`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

/**
 * Detailed per-client activity report (breakdown by type, user, day).
 */
const GetClientActivityReport = async (payload, authToken) => {
    const response = await fetch(`${API_URL}/client-activity-report`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

const GetClientActivityReportPdf = async (payload, authToken) => {
    const response = await fetch(`${API_URL}/client-activity-report-html`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return response;
};

const UserActivityService = {
    GetTotalActivity,
    GetSubAccountantsTotals,
    GetDailySummaries,
    GetDailyByUser,
    FilterDailyMouseRecords,
    GetEmployeeEfficiencyReport,
    GetBusinessEfficiencyReport,
    GetEmployeeEfficiencyReportHtml,
    GetBusinessEfficiencyReportHtml,
    GetEmployeeEfficiencyReportExcel,
    GetBusinessEfficiencyReportExcel,
    GetClientActivityReport,
    GetClientActivityReportPdf,
};

export default UserActivityService;
